// src/pages/JobDetails.js
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getJobRoleById, submitApplication } from '../api/careersApi';
import LoadingSpinner from '../components/LoadingSpinner';
import './JobDetails.css';

const JobDetails = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState({ name: '', email: '', resume: null });
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  // Memoized fetch function to satisfy ESLint dependency requirement
  const fetchJobDetails = useCallback(async () => {
    try {
      const res = await getJobRoleById(id);
      setJob(res.data);
    } catch (err) {
      console.error('Error fetching job details:', err);
      setError('Failed to load job details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Call the memoized fetch function on component mount or id change
  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!application.resume) {
      setError('Please upload your resume.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append(
        'data',
        JSON.stringify({
          applicantName: application.name,
          email: application.email,
          jobRoleId: job.id,
        })
      );
      formData.append('file', application.resume);

      await submitApplication(formData);
      setMsg('Application submitted successfully!');
      setApplication({ name: '', email: '', resume: null });
      setError('');
    } catch (err) {
      console.error(err);
      setError('Failed to submit application. Please try again.');
      setMsg('');
    }
  };

  return (
    <div className="job-details-container">
      {loading ? (
        <LoadingSpinner />
      ) : job ? (
        <div className="job-details-wrapper">
          {/* Left Side: Job Information */}
          <div className="job-info">
            <header className="job-header">
              <h1 className="job-title">{job.title}</h1>
              <div className="job-meta">
                <span>{job.type || 'Not specified'}</span>
                <span>{job.location || 'Location not specified'}</span>
                {job.salary && <span>{job.salary}</span>}
              </div>
            </header>

            {job.summary && <p className="job-summary">{job.summary}</p>}

            <section className="job-section">
              <h3>Description</h3>
              <p>{job.description}</p>
            </section>

            {job.requirements && (
              <section className="job-section">
                <h3>Requirements</h3>
                <ul>
                  {job.requirements.split('\n').map((req, idx) => (
                    <li key={idx}>{req}</li>
                  ))}
                </ul>
              </section>
            )}

            {job.benefits && (
              <section className="job-section">
                <h3>Benefits</h3>
                <ul>
                  {job.benefits.split('\n').map((benefit, idx) => (
                    <li key={idx}>{benefit}</li>
                  ))}
                </ul>
              </section>
            )}

            {job.aboutCompany && (
              <section className="job-section">
                <h3>About Us</h3>
                <p>{job.aboutCompany}</p>
              </section>
            )}
          </div>

          {/* Right Side: Application Form */}
          <aside className="job-application">
            <h2 className="apply-heading">Apply for this Role</h2>
            {msg && <p className="form-msg success">{msg}</p>}
            {error && <p className="form-msg error">{error}</p>}

            <form onSubmit={handleSubmit} className="application-form">
              <label>
                Full Name
                <input
                  type="text"
                  value={application.name}
                  onChange={(e) => setApplication({ ...application, name: e.target.value })}
                  required
                />
              </label>

              <label>
                Email Address
                <input
                  type="email"
                  value={application.email}
                  onChange={(e) => setApplication({ ...application, email: e.target.value })}
                  required
                />
              </label>

              <label>
                Resume (PDF/DOC)
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setApplication({ ...application, resume: e.target.files[0] })}
                  required
                />
              </label>

              <button type="submit" className="apply-btn">
                Submit Application
              </button>
            </form>
          </aside>
        </div>
      ) : (
        <p>{error || 'Job not found.'}</p>
      )}
    </div>
  );
};

export default JobDetails;
